import { useMemo, useState } from 'react'
import { Alert, Button, Group, Select, Stack, Text } from '@mantine/core'
import { IconAlertTriangle, IconUserMinus, IconUserPlus } from '@tabler/icons-react'
import { isAxiosError } from 'axios'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import { atribuirDono, desatribuirDono, type RoboFrota } from '@/api/robo'
import { selectableUsers, type MixedUserRow } from '@/utils/realUsers'

export type UsuarioLista = MixedUserRow & {
  full_name?: string
  email?: string
  is_active?: boolean | number
}

/**
 * As recusas do backend viram frase.
 *
 * A tela NAO tenta prever os conflitos. `funcionario_ja_tem_robo` e' garantido
 * tambem pelo indice parcial `ux_robos_owner_vivo`, entao a condicao de corrida
 * e' real: entre listar e atribuir, outra sessao pode ter atribuido a mesma
 * pessoa. A verdade e' a resposta.
 *
 * Codigo desconhecido NAO vira frase inventada — ele aparece como veio, porque
 * um texto amigavel sobre uma causa que eu nao conheco e' pior que o codigo.
 */
const FRASES: Record<string, string> = {
  robo_ja_tem_dono:
    'Este robô já tem dono. Remova o dono atual antes de atribuir a outra pessoa — '
    + 'transferir são duas decisões, e as duas ficam na trilha.',
  funcionario_ja_tem_robo:
    'Esta pessoa já é dona de outro robô, inclusive se aquele robô estiver aposentado. '
    + 'Cada funcionário tem um robô só.',
  usuario_nao_atribuivel:
    'Esta conta não pode ser dona de um robô: contas de serviço e usuários inativos ficam de fora.',
  dono_sem_acesso_ao_tenant:
    'Esta pessoa não tem acesso ao cliente que o robô observa, e o robô exibiria alertas que ela não pode ver.',
  device_not_found:
    'Este robô não existe mais na frota. Recarregue a frota.',
}

export function frasePorErro(status: number, detail: string): string {
  return FRASES[detail] ?? `O Console recusou a atribuição (${status} ${detail}).`
}

function detalheDoErro(erro: unknown): string {
  if (!isAxiosError(erro) || !erro.response) return 'Não foi possível falar com o Console.'
  const detail = (erro.response.data as { detail?: unknown })?.detail
  return frasePorErro(erro.response.status, typeof detail === 'string' ? detail : 'sem_detalhe')
}

/**
 * A lista de usuários que a tela do robô consulta. Exportada para o "Começar de
 * novo" nomear o dono com a MESMA `queryKey` — duas chaves para a mesma lista
 * seriam duas consultas e dois caches que podem discordar.
 */
export function useUsuariosParaRobo(enabled = true) {
  return useQuery({
    queryKey: ['usuarios-para-robo'],
    // `/v1/users` devolve `{items: [...]}`, nunca um array — ver Frota.tsx.
    queryFn: async (): Promise<UsuarioLista[]> =>
      (await api.get<{ items: UsuarioLista[] }>('/v1/users')).data.items ?? [],
    staleTime: 5 * 60 * 1000,
    enabled,
  })
}

export function AtribuirDono(
  { robo, onMudou }: { robo: RoboFrota; onMudou: () => void },
) {
  const [escolhido, setEscolhido] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const { data: usuarios } = useUsuariosParaRobo()

  // `selectableUsers` tira os cadastros PENDENTES (`id: null`): dois deles
  // viravam duas opcoes de valor "null" e o Mantine derrubava a arvore com
  // `Duplicate options are not supported` — em producao (24/08/2026).
  const opcoes = useMemo(
    () => selectableUsers(usuarios ?? [])
      .filter((u) => u.is_active === undefined || !!u.is_active)
      .map((u) => ({ value: String(u.id), label: u.full_name || u.email || `usuário #${u.id}` })),
    [usuarios],
  )

  const nomeDoDono = useMemo(() => {
    if (!robo.owner_user_id) return null
    return opcoes.find((o) => o.value === String(robo.owner_user_id))?.label
      ?? `usuário #${robo.owner_user_id}`
  }, [opcoes, robo.owner_user_id])

  const executa = async (acao: () => Promise<unknown>) => {
    setErro(null)
    setOcupado(true)
    try {
      await acao()
      setEscolhido(null)
      onMudou()
    } catch (e) {
      setErro(detalheDoErro(e))
    } finally {
      setOcupado(false)
    }
  }

  return (
    <Stack gap="sm">
      {erro && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />} onClose={() => setErro(null)} withCloseButton>
          {erro}
        </Alert>
      )}

      {robo.owner_user_id
        ? (
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={2}>
              <Text size="sm">{nomeDoDono}</Text>
              <Text size="xs" c="dimmed">
                {robo.owner_named_at
                  ? 'Já batizou o robô — o chat está aberto.'
                  : 'Ainda não batizou o robô. O chat abre depois que o dono der um nome a ele.'}
              </Text>
            </Stack>
            <Button
              variant="light"
              color="red"
              loading={ocupado}
              leftSection={<IconUserMinus size={16} />}
              onClick={() => executa(() => desatribuirDono(robo.public_id))}
            >
              Remover dono
            </Button>
          </Group>
          )
        : (
          <Group align="flex-end" wrap="nowrap">
            <Select
              flex={1}
              label="Dono"
              placeholder="Escolha um funcionário"
              description="Quem recebe o robô passa a ajustá-lo — nome, e mais adiante personalidade e o que ele monitora."
              searchable
              data={opcoes}
              value={escolhido}
              onChange={setEscolhido}
            />
            <Button
              disabled={!escolhido}
              loading={ocupado}
              leftSection={<IconUserPlus size={16} />}
              onClick={() => executa(() => atribuirDono(robo.public_id, Number(escolhido)))}
            >
              Atribuir
            </Button>
          </Group>
          )}
    </Stack>
  )
}
